import { HttpError } from '@euler/lib/request/HttpError';
import {
  ApdmPlateCouponBatchRedeemInput,
  ApdmPlateCouponBatchRedeemLine,
  ApdmPlateCouponBatchRedeemOutcome,
  ApdmPlateCouponItemsResult,
  ApdmPlateCouponState,
  CouponRecipientQrInput,
  kApdmPlateCouponBatchMaxCoupons,
  kApdmPlateCouponBatchMaxLines,
} from '@euler/model/apdm';
import { useServiceFactory } from '@euler/services/factory';
import { makeUniqueId } from '@euler/utils/id';
import { useEffect, useRef, useState } from 'react';
import {
  ApdmCouponPicks,
  apdmCouponBatchLines,
  initialApdmCouponPicks,
  parseApdmCouponQr,
} from './couponRedemption';

export enum ApdmRedemptionPhase {
  /** No coupon read yet, or the last read failed. */
  Scan = 'scan',
  Reading = 'reading',
  /** A coupon is shown and items can be picked. */
  Ready = 'ready',
  Redeeming = 'redeeming',
  Done = 'done',
  /** The last request's result is unknown; only a retry of it is allowed. */
  Unsure = 'unsure',
}

export interface ApdmRedemptionDone {
  redeemedAt: string;
  lines: ApdmPlateCouponBatchRedeemLine[];
}

export interface ApdmRedemptionState {
  phase: ApdmRedemptionPhase;
  coupon: ApdmPlateCouponItemsResult | null;
  picks: ApdmCouponPicks;
  done: ApdmRedemptionDone | null;
  /** The coupon changed elsewhere and was read again. */
  changed: boolean;
  error: string | null;
}

const kIdle: ApdmRedemptionState = {
  phase: ApdmRedemptionPhase.Scan,
  coupon: null,
  picks: {},
  done: null,
  changed: false,
  error: null,
};

/**
 * The server answered and refused: nothing was redeemed. Anything else (no
 * answer, a timeout, a server fault) leaves the outcome unknown.
 */
function definiteRefusal(error: unknown): error is HttpError {
  return (
    error instanceof HttpError &&
    ((error.status >= 400 && error.status < 500) ||
      (error.status >= 200 && error.status < 300))
  );
}

function pickedCoupons(picks: ApdmCouponPicks, except?: string): number {
  return Object.entries(picks)
    .filter(([id]) => id !== except)
    .reduce((sum, [, count]) => sum + count, 0);
}

/** The most of an item that can still be picked within one batch. */
export function apdmCouponPickLimit(
  left: number,
  picks: ApdmCouponPicks,
  itemId: string,
): number {
  return Math.min(
    left,
    kApdmPlateCouponBatchMaxCoupons - pickedCoupons(picks, itemId),
  );
}

/** The coupon as it is after a batch landed, without reading it again. */
function afterBatch(
  coupon: ApdmPlateCouponItemsResult,
  lines: readonly ApdmPlateCouponBatchRedeemLine[],
): ApdmPlateCouponItemsResult {
  const used = new Set(lines.flatMap(line => line.couponIds));
  const items = coupon.items.map(item => {
    const left = item.claimableCouponIds.filter(id => !used.has(id));
    const spent = item.claimableCouponIds.length - left.length;
    return {
      ...item,
      claimableCouponIds: left,
      remainingQty: left.length,
      redeemedQty: item.redeemedQty + spent,
      nextCouponId: left[0] ?? null,
    };
  });
  return {
    ...coupon,
    items,
    state: items.some(item => item.claimableCouponIds.length)
      ? coupon.state
      : ApdmPlateCouponState.UsedUp,
  };
}

export function useApdmCouponRedemption(storeId: number) {
  const { apdmCouponService: service } = useServiceFactory();
  const [state, setState] = useState<ApdmRedemptionState>(kIdle);
  const scan = useRef<CouponRecipientQrInput | null>(null);
  // The request whose result is unknown: a retry resends exactly this.
  const pending = useRef<ApdmPlateCouponBatchRedeemInput | null>(null);
  const working = useRef(false);
  const generation = useRef(0);
  const activeStore = useRef<number | null>(storeId);
  useEffect(() => {
    const epoch = generation;
    const scope = activeStore;
    scope.current = storeId;
    epoch.current++;
    scan.current = null;
    pending.current = null;
    working.current = false;
    setState(kIdle);
    return () => {
      scope.current = null;
      epoch.current++;
    };
  }, [storeId, service]);

  const current = () => activeStore.current === storeId;
  const readCoupon = async (identity: CouponRecipientQrInput) => {
    const coupon = await service.items(identity);
    if (coupon.couponCampaignId !== identity.couponCampaignId)
      throw new Error('Unrelated coupon items');
    return coupon;
  };

  const load = async (raw: string) => {
    if (!current() || working.current || pending.current) return;
    const identity = parseApdmCouponQr(raw);
    if (!identity) {
      scan.current = null;
      setState({
        ...kIdle,
        error: '二维码无效，请扫描客户的优惠券二维码。',
      });
      return;
    }
    const epoch = ++generation.current;
    working.current = true;
    scan.current = identity;
    setState({ ...kIdle, phase: ApdmRedemptionPhase.Reading });
    try {
      const coupon = await readCoupon(identity);
      if (epoch !== generation.current) return;
      setState({
        ...kIdle,
        phase: ApdmRedemptionPhase.Ready,
        coupon,
        picks: initialApdmCouponPicks(coupon.items),
      });
    } catch {
      if (epoch !== generation.current) return;
      scan.current = null;
      setState({
        ...kIdle,
        error: '读取失败：优惠券不属于本店、已失效，或网络异常，请重试。',
      });
    } finally {
      if (epoch === generation.current) working.current = false;
    }
  };

  /** Checks or unchecks an item; a checked item starts at one. */
  const toggle = (itemId: string) => {
    if (!current() || working.current) return;
    setState(prev => {
      if (prev.phase !== ApdmRedemptionPhase.Ready || !prev.coupon) return prev;
      const item = prev.coupon.items.find(entry => entry.itemId === itemId);
      if (!item?.claimableCouponIds.length) return prev;
      if (
        !prev.picks[itemId] &&
        (Object.keys(prev.picks).length >= kApdmPlateCouponBatchMaxLines ||
          pickedCoupons(prev.picks) >= kApdmPlateCouponBatchMaxCoupons)
      )
        return prev;
      const picks = prev.picks[itemId]
        ? Object.fromEntries(
            Object.entries(prev.picks).filter(([id]) => id !== itemId),
          )
        : { ...prev.picks, [itemId]: 1 };
      return { ...prev, picks, changed: false };
    });
  };

  const setCount = (itemId: string, count: number) => {
    if (!current() || working.current) return;
    setState(prev => {
      if (prev.phase !== ApdmRedemptionPhase.Ready || !prev.coupon) return prev;
      const item = prev.coupon.items.find(entry => entry.itemId === itemId);
      if (!item || !prev.picks[itemId]) return prev;
      const bounded = Math.max(
        1,
        Math.min(
          apdmCouponPickLimit(
            item.claimableCouponIds.length,
            prev.picks,
            itemId,
          ),
          Math.round(count),
        ),
      );
      return { ...prev, picks: { ...prev.picks, [itemId]: bounded } };
    });
  };

  const send = async (request: ApdmPlateCouponBatchRedeemInput) => {
    const epoch = generation.current;
    const identity = scan.current!;
    pending.current = request;
    working.current = true;
    setState(prev => ({
      ...prev,
      phase: ApdmRedemptionPhase.Redeeming,
      changed: false,
      error: null,
    }));
    try {
      const result = await service.redeem(request);
      if (result.couponCampaignId !== request.couponCampaignId)
        throw new Error('Unrelated coupon receipt');
      if (epoch !== generation.current) return;
      pending.current = null;
      if (result.outcome === ApdmPlateCouponBatchRedeemOutcome.Changed) {
        const coupon = await readCoupon(identity).catch(() => null);
        if (epoch !== generation.current) return;
        setState({
          ...kIdle,
          phase: coupon ? ApdmRedemptionPhase.Ready : ApdmRedemptionPhase.Scan,
          coupon,
          changed: !!coupon,
          error: coupon ? null : '优惠券已变化，请重新扫描。',
        });
        if (!coupon) scan.current = null;
        return;
      }
      setState(prev => ({
        ...prev,
        phase: ApdmRedemptionPhase.Done,
        coupon: prev.coupon && afterBatch(prev.coupon, request.lines),
        picks: {},
        done: { redeemedAt: result.redeemedAt, lines: request.lines },
      }));
    } catch (error) {
      if (epoch !== generation.current) return;
      if (definiteRefusal(error)) {
        pending.current = null;
        const message = error.message || '核销未完成，请重试或重新扫描。';
        setState(prev => ({
          ...prev,
          phase: ApdmRedemptionPhase.Ready,
          error: message,
        }));
      } else setState(prev => ({ ...prev, phase: ApdmRedemptionPhase.Unsure }));
    } finally {
      if (epoch === generation.current) working.current = false;
    }
  };

  const redeem = async () => {
    if (!current() || working.current || !scan.current) return;
    if (pending.current) {
      await send(pending.current);
      return;
    }
    if (state.phase !== ApdmRedemptionPhase.Ready || !state.coupon) return;
    let lines: ApdmPlateCouponBatchRedeemLine[] = [];
    try {
      lines = apdmCouponBatchLines(state.coupon.items, state.picks);
    } catch {
      return;
    }
    if (!lines.length) return;
    // One id per batch: a retry resends this request as it is.
    await send({ ...scan.current, requestId: makeUniqueId(24), lines });
  };

  /** Reads the same coupon again to redeem more of it. */
  const continueCoupon = async () => {
    if (!current() || working.current || pending.current || !scan.current)
      return;
    const { couponCampaignId, recipientToken } = scan.current;
    // The scanned value is not kept; rebuild the read from its identity.
    const epoch = ++generation.current;
    working.current = true;
    setState(prev => ({ ...prev, phase: ApdmRedemptionPhase.Reading }));
    try {
      const coupon = await readCoupon({ couponCampaignId, recipientToken });
      if (epoch !== generation.current) return;
      setState({
        ...kIdle,
        phase: ApdmRedemptionPhase.Ready,
        coupon,
        picks: initialApdmCouponPicks(coupon.items),
      });
    } catch {
      if (epoch === generation.current)
        setState(prev => ({
          ...prev,
          phase: ApdmRedemptionPhase.Done,
          error: '读取失败，请重新扫描。',
        }));
    } finally {
      if (epoch === generation.current) working.current = false;
    }
  };

  /** Back to an empty page for the next customer's coupon. */
  const reset = () => {
    if (!current() || working.current || pending.current) return;
    generation.current++;
    scan.current = null;
    setState(kIdle);
  };

  /**
   * Gives up on a batch whose outcome is unknown. Safe: the next read of
   * the coupon shows whether it landed.
   */
  const abandon = () => {
    if (!current() || working.current || !pending.current) return;
    pending.current = null;
    reset();
  };

  return {
    state,
    actions: { load, toggle, setCount, redeem, continueCoupon, reset, abandon },
  };
}
